'use client';

import { Card } from '@/components/cms/Card';
import { CMSSection } from '@/components/cms/CmsSection';
import { MediaWrapper } from '@/components/cms/MediaWrapper';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import type { CMSContentItem } from '@/lib/cms/types';
import type {
  ContentfulCardFragment,
  ContentGroupFragment,
  ImageWithFocalPoint,
} from '@/lib/contentful/types';
import { filterCollection } from '@/lib/contentful/utils';
import clsx from 'clsx';
import styles from './FourColumnScroll.module.scss';

export interface FourColumnScrollProps {
  /**
   * Contentful ContentGroup data with Card, MediaWrapper, or ProductCard items (legacy format)
   */
  data?: ContentGroupFragment;
  /**
   * Direct items array (adapter format)
   * Takes precedence over data.itemsCollection if provided
   */
  items?: CMSContentItem[];
  /** Optional extra data for items */
  extraData?: Record<string, unknown>;
  /** CMS index for unique data-testid generation */
  cmsIndex?: number;
  /** Optional className */
  className?: string;
}

interface ImageWithFocalPointItem extends ImageWithFocalPoint {
  __typename: 'ImageWithFocalPoint';
}

type ItemData = ContentfulCardFragment | ImageWithFocalPointItem;

/**
 * FourColumnScroll - 4-column card/media layout
 *
 * Renders items in a responsive layout:
 * - Mobile: Horizontal scrolling flex container
 * - Desktop: 4-column CSS grid
 *
 * Supports Card, MediaWrapper (ImageWithFocalPoint), and ProductCard content types.
 *
 * Features:
 * - Smooth horizontal scroll on mobile with snap
 * - 4-column grid on desktop
 * - Supports multiple item types
 * - Equal-height items in grid mode
 *
 * @example
 * <FourColumnScroll
 *   data={contentGroupData}
 *   cmsIndex={0}
 * />
 */
export function FourColumnScroll({
  data,
  items: directItems,
  cmsIndex,
  className,
}: FourColumnScrollProps) {
  const isMobile = useMediaQuery('(max-width: 767px)');
  // Prefer direct items (adapter format), fallback to data.itemsCollection (legacy format)
  const items =
    directItems ?? (data?.itemsCollection ? filterCollection(data.itemsCollection) : []);

  const testId = (element?: string) =>
    cmsIndex !== undefined
      ? element
        ? `fourcolumnscroll-${cmsIndex}-${element}`
        : `fourcolumnscroll-${cmsIndex}`
      : undefined;

  if (items.length === 0) {
    return null;
  }

  const renderItem = (item: ItemData, index: number) => {
    const typename = item.__typename;
    const sysId = item.sys?.id;

    const itemClassName = isMobile ? styles.flexItem : styles.gridItem;

    switch (typename) {
      case 'Card':
        return (
          <div key={sysId || index} className={itemClassName} data-testid={testId(`card-${index}`)}>
            <Card
              type={(item as ContentfulCardFragment).type as never}
              data={item}
              preload
              cmsIndex={index}
            />
          </div>
        );

      case 'ImageWithFocalPoint': {
        // Cast item to ImageWithFocalPoint and pass directly to MediaWrapper
        return (
          <div
            key={sysId || index}
            className={itemClassName}
            data-testid={testId(`image-${index}`)}
          >
            <MediaWrapper media={item as ImageWithFocalPoint} mediaType="Grid" />
          </div>
        );
      }

      // ProductCard can be added here if needed
      // case 'ProductCard':
      //   return ...

      default:
        return null;
    }
  };

  return (
    <CMSSection className={clsx(styles.section, className)} data-testid={testId()}>
      <div className={styles.container}>
        {isMobile ? (
          <div className={styles.flexWrapper} data-testid={testId('flex-wrapper')}>
            {items.map((item, index) => renderItem(item as ItemData, index))}
          </div>
        ) : (
          <div className={styles.gridWrapper} data-testid={testId('grid-wrapper')}>
            {items.map((item, index) => renderItem(item as ItemData, index))}
          </div>
        )}
      </div>
    </CMSSection>
  );
}

FourColumnScroll.displayName = 'FourColumnScroll';
